import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Box,
  Text,
  Icon,
  Flex,
} from '@chakra-ui/react';
import { MapPin } from 'lucide-react';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  LOCATION_MAP_IFRAME_SANDBOX,
  resolveMapDisplay,
} from 'components/LocationMap/locationMapEmbed';
import { OpenMapInMapsLink } from 'components/LocationMap/OpenMapInMapsLink';

interface LocationMapModalProps {
  isOpen: boolean;
  onClose: () => void;
  locationHtml: string;
  locationName: string;
}

export const LocationMapModal = ({
  isOpen,
  onClose,
  locationHtml,
  locationName,
}: LocationMapModalProps) => {
  const { formatMessage } = useIntl();
  const display = resolveMapDisplay(locationHtml);
  const eventLocationFallback = formatMessage({
    id: 'locationMap.eventLocation',
    defaultMessage: 'Event location',
  });
  const iframeTitleFallback = formatMessage({
    id: 'locationMap.iframeTitle',
    defaultMessage: 'Location map',
  });
  const mapLabelLocation = locationName || eventLocationFallback;

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size='4xl'>
      <ModalOverlay bg='blackAlpha.600' backdropFilter='blur(4px)' />
      <ModalContent
        mx={{ base: '4', md: '6' }}
        maxW={{ base: 'calc(100vw - 32px)', md: '900px', lg: '1000px' }}
        borderRadius={{ base: 'lg', md: 'xl' }}
        overflow='hidden'
      >
        <ModalHeader
          py={{ base: '3', md: '4' }}
          px={{ base: '4', md: '6' }}
          bg='gray.50'
          borderBottomWidth='1px'
          borderBottomColor='gray.200'
        >
          <Flex align='center' gap='3' justifyContent={'center'}>
            <Icon
              as={MapPin}
              w={{ base: '5', md: '6' }}
              h={{ base: '5', md: '6' }}
              color='red.500'
            />
            <Text
              fontSize={{ base: 'md', md: 'lg' }}
              fontWeight='semibold'
              color='gray.800'
              noOfLines={1}
            >
              {locationName || eventLocationFallback}
            </Text>
          </Flex>
        </ModalHeader>
        <ModalCloseButton
          top={{ base: '2', md: '3' }}
          right={{ base: '2', md: '4' }}
          _hover={{ bg: 'gray.100' }}
          _focus={{ boxShadow: 'outline' }}
        />
        <ModalBody p={0}>
          <Box
            h={{ base: '300px', sm: '400px', md: '500px', lg: '550px' }}
            w='100%'
            position='relative'
          >
            {display?.kind === 'iframe' ? (
              <iframe
                src={display.src}
                title={locationName || iframeTitleFallback}
                style={{
                  width: '100%',
                  height: '100%',
                  border: 'none',
                  display: 'block',
                }}
                loading='lazy'
                referrerPolicy='no-referrer-when-downgrade'
                sandbox={LOCATION_MAP_IFRAME_SANDBOX}
                aria-label={formatMessage(
                  {
                    id: 'locationMap.mapAriaLabel',
                    defaultMessage: 'Map showing {location}',
                  },
                  { location: mapLabelLocation },
                )}
                allowFullScreen
              />
            ) : display?.kind === 'externalLink' ? (
              <OpenMapInMapsLink href={display.href} />
            ) : (
              <Flex
                w='100%'
                h='100%'
                align='center'
                justify='center'
                bg='gray.100'
                color='gray.600'
              >
                <Text fontSize='sm'>
                  <FormattedMessage
                    id='locationMap.loadFailed'
                    defaultMessage='Unable to load the map'
                  />
                </Text>
              </Flex>
            )}
          </Box>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
